import { Paper, type PaperProps } from '@mui/material';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import Draggable, { type DraggableEventHandler } from 'react-draggable';

import { aiAssistantContext } from '../../../state/AIAssistantContextProvider';
import { calculateDraggableBoundaries } from '../shared/utils';

const AIAssistantDraggable = (props: PaperProps) => {
	const nodeRef = React.useRef<HTMLDivElement>(null);

	const {
		assistantPosition,
		setAssistantPosition,
		draggableBounds,
		setDraggableBounds,
		isExtended,
		setIsAssistantDragging,
	} = useContext(aiAssistantContext);
	const lastIsExtendedState = useRef(isExtended);

	const recalculateDraggableBounds = useCallback(() => {
		if (!nodeRef.current) return;

		const { offsetHeight, offsetWidth } = nodeRef.current;
		const calculatedDraggableBounds = calculateDraggableBoundaries({
			windowWidth: window.innerWidth,
			windowHeight: window.innerHeight,
			elementWidth: offsetWidth,
			elementHeight: offsetHeight,
		});

		setDraggableBounds(calculatedDraggableBounds);
	}, [setDraggableBounds]);

	useEffect(() => {
		// calculate initial draggable bounds
		const areDraggableBoundsSet = !Object.values(draggableBounds).every((value) => value === 0);
		if (nodeRef.current && !areDraggableBoundsSet) {
			recalculateDraggableBounds();
		}
	}, [draggableBounds, recalculateDraggableBounds]);

	useEffect(() => {
		// make sure the draggable dialog is not out of the screen on resize
		if (!nodeRef.current) return;
		if (lastIsExtendedState.current === isExtended) return;

		// Recalculate bounds when dialog width changes
		recalculateDraggableBounds();

		lastIsExtendedState.current = isExtended;
	}, [isExtended, recalculateDraggableBounds]);

	useEffect(() => {
		// make sure the draggable dialog is not dragged out of the screen
		if (!nodeRef.current) return;

		if (assistantPosition.x < draggableBounds.left) {
			setAssistantPosition({ ...assistantPosition, x: draggableBounds.left });
		}
	}, [draggableBounds, assistantPosition, setAssistantPosition]);

	useEffect(() => {
		window.addEventListener('resize', recalculateDraggableBounds);
		return () => {
			window.removeEventListener('resize', recalculateDraggableBounds);
		};
	}, [recalculateDraggableBounds]);

	const onStart: DraggableEventHandler = () => {
		setIsAssistantDragging(true);
	};

	const onStop: DraggableEventHandler = (_, { x, y }) => {
		setAssistantPosition({ x, y });
		setIsAssistantDragging(false);
	};

	return (
		<Draggable
			nodeRef={nodeRef as React.RefObject<HTMLDivElement>}
			handle=".draggable-dialog-title"
			cancel={'[class*="MuiDialogContent-root"]'}
			bounds={draggableBounds}
			onStart={onStart}
			onStop={onStop}
			position={assistantPosition}
			enableUserSelectHack
		>
			<Paper data-testid="ai-assistant-draggable" {...props} ref={nodeRef} />
		</Draggable>
	);
};

export default AIAssistantDraggable;
